import React, {
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
// Import the new hook instead of the old one
import useCartLocationStatus from "../../hooks/useCartLocationStatus";
import { trackEvent, trackEcommerceEvent } from "../../hooks/useGTM";
import CartGroup from "./components/CartGroup/CartGroup";
import CartSummary from "./components/CartSummary/CartSummary";
import EmptyCartView from "./components/EmptyCartView/EmptyCartView";
import LocationInfo from "./components/LocationInfo/LocationInfo";
import CartSkeleton from "./components/CartSkeleton/CartSkeleton";
import LoadingOverlay from "./components/LoadingOverlay/LoadingOverlay";
import {
  fetchCart,
  validateCoupon,
  removeCoupon,
  clearCouponError,
} from "../../redux/cartReducer";
import { handleCartItemUpdate } from "../../utils/cartUpdateUtils";
import { validateAndReserveStock } from "../../lib/cartGroupApi";
import { validateCartStock } from "../../lib/cartApis";
import CouponCodeInput from "../../components/CouponCodeInput/CouponCodeInput"; // Added import
import styles from "./Cart.module.css";

// Define proper dispatch type to fix dispatch errors
type AppDispatch = ThunkDispatch<any, any, AnyAction>;

const Cart: React.FC = () => {
  // Use the new CartLocationStatus hook which works with GlobalCartSyncService
  const { isLocationUpdating } = useCartLocationStatus();

  // Get cart state from Redux
  const cart = useSelector((state: any) => state.cart);
  const {
    groups: cartGroups,
    loading,
    error,
    cartId,
    // Coupon state
    appliedCouponCode,
    couponDiscountAmount,
    couponDetails,
    couponError,
    couponLoading,
  } = cart;
  const dispatch = useDispatch<AppDispatch>();

  // Scroll to top on component mount
  useLayoutEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "auto", // Use "auto" instead of "smooth" for immediate scroll
    });
  }, []);

  // Stock validation states
  const [isCheckoutDisabled, setIsCheckoutDisabled] = useState<boolean>(false);
  const [stockValidationErrors, setStockValidationErrors] = useState<any[]>([]);
  const [stockValidation, setStockValidation] = useState({
    isLoading: false,
    isValid: true,
    results: [],
    lastChecked: 0,
  });

  // Get location from Redux
  const location = useSelector((state: any) => state.location);

  // Load cart data if necessary
  useEffect(() => {
    if (!loading && !cartGroups && localStorage.getItem("cartId")) {
      dispatch(fetchCart());
    }

    // Track cart page view
    trackEvent("cart_page_view", {
      page_title: "Cart Page",
      page_section: "cart",
    });

    // Track begin_checkout event if cart has items
    if (cartGroups && cartGroups.length > 0) {
      const cartItems = cartGroups.flatMap((group: any) =>
        group.items.map((item: any) => ({
          item_id: item.product?.id,
          item_name: item.product?.name || "Unknown Product",
          price: item.price?.discount?.newPrice || item.price || 0,
          quantity: item.quantity,
        }))
      );

      if (cartItems.length > 0) {
        trackEcommerceEvent("begin_checkout", {
          currency: "PEN",
          value: cartGroups.reduce(
            (sum: number, group: any) => sum + (group.groupTotal || 0),
            0
          ),
          items: cartItems,
        });
      }
    }
  }, [dispatch, cartGroups, loading]);

  // Update checkout button state based on cart contents
  useEffect(() => {
    // Check for cart groups without delivery dates
    const hasGroupsWithoutDates = cartGroups?.some(
      (group: any) =>
        !group.deliveryDate && group.items.length > 0 && location.address
    );

    const hasUnavailableDelivery = cartGroups?.some(
      (group: any) =>
        group.deliveryFee === -1 || group.deliveryFee === undefined
    );

    // Disable checkout when there are groups without dates or unavailable delivery
    setIsCheckoutDisabled(
      hasUnavailableDelivery || !location.address || hasGroupsWithoutDates
    );
  }, [cartGroups, location]);

  // Asynchronously validate stock after cart loads
  useEffect(() => {
    // Only run validation if we have cart data and it's been at least 30 seconds since last check
    const now = Date.now();
    if (cartId && now - stockValidation.lastChecked > 30000) {
      const validateCartStockAsync = async () => {
        setStockValidation((prev) => ({ ...prev, isLoading: true }));

        try {
          // Call the new endpoint with a slight delay to prioritize UI rendering
          setTimeout(async () => {
            try {
              const response = await validateCartStock(cartId);

              setStockValidation({
                isLoading: false,
                isValid: response.isValid,
                results: response.validationResults || [],
                lastChecked: Date.now(),
              });

              // If there are stock issues, show a notification to the user
              if (!response.isValid) {
                console.log(
                  "Stock validation failed:",
                  response.validationResults
                );
              }
            } catch (error) {
              console.error("Failed to validate stock:", error);
              setStockValidation((prev) => ({
                ...prev,
                isLoading: false,
              }));
            }
          }, 1000); // 1 second delay
        } catch (error) {
          console.error("Failed to setup stock validation:", error);
          setStockValidation((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      };

      validateCartStockAsync();
    }
  }, [cartId, stockValidation.lastChecked]);

  // Handle delivery date change
  const handleDeliveryDateChange = async (
    groupId: string,
    deliveryDate: string
  ) => {
    console.log("Date change processed for group", groupId, deliveryDate);

    // Force-refresh cart after date change to ensure all calculated values are updated
    setTimeout(() => {
      refreshCart();
    }, 300);
  };

  // Handle quantity changes with improved behavior
  const handleCartAmountChange = async (newQuantity: number, item: any) => {
    // Prevent changes if location update is in progress
    if (isLocationUpdating) {
      console.log("[CART] Ignoring quantity change while location is updating");
      return;
    }

    // Ensure item has groupId
    if (!item.groupId) {
      console.error("Missing groupId for item:", item);

      // Try to find the group containing this item
      const group = cartGroups.find((g: any) =>
        g.items.some((p: any) => p.cartCustomId === item.cartCustomId)
      );

      if (group) {
        item.groupId = group.id;
        console.log(`[CART] Found and added groupId ${group.id} to item`);
      } else {
        console.error(
          "[CART] Failed to find group for item:",
          item.cartCustomId
        );
        return;
      }
    }

    // Use the simplified cart update logic from utils
    await handleCartItemUpdate(newQuantity, item, dispatch);
  };

  // New handler specifically for removing items
  const handleRemoveItem = useCallback(
    async (item: any) => {
      // Prevent removals if location update is in progress
      if (isLocationUpdating) {
        console.log("[CART] Ignoring item removal while location is updating");
        return;
      }

      // Ensure item has groupId
      if (!item.groupId) {
        console.error("Missing groupId for item:", item);

        // Try to find the group containing this item
        const group = cartGroups.find((g: any) =>
          g.items.some((p: any) => p.cartCustomId === item.cartCustomId)
        );

        if (group) {
          item.groupId = group.id;
          console.log(`[CART] Found and added groupId ${group.id} to item`);
        } else {
          console.error(
            "[CART] Failed to find group for item:",
            item.cartCustomId
          );
          return;
        }
      }

      // Use handleCartItemUpdate with quantity=0
      await handleCartItemUpdate(0, item, dispatch);
    },
    [dispatch, isLocationUpdating, cartGroups]
  );

  // State for checkout validation
  const [checkoutValidationStatus, setCheckoutValidationStatus] = useState({
    isValidating: false,
    isComplete: false,
    message: "",
  });

  // Handle checkout validation
  const handleCheckoutValidation = async () => {
    // Prevent checkout if location update is in progress
    if (isLocationUpdating) {
      console.log(
        "[CART] Ignoring checkout request while location is updating"
      );
      return {
        isValid: false,
        errors: [
          "Por favor espere mientras se actualiza la información de envío",
        ],
      };
    }

    // Check for groups without delivery dates - prevent checkout
    const groupsWithoutDates = cartGroups.filter(
      (group: any) => !group.deliveryDate && group.items.length > 0
    );

    if (groupsWithoutDates.length > 0) {
      return {
        isValid: false,
        errors: [
          "Por favor selecciona una fecha de entrega para todos los grupos",
        ],
      };
    }

    // Continue with the rest of the validation
    try {
      // Show validation in progress
      setCheckoutValidationStatus({
        isValidating: true,
        isComplete: false,
        message: "Validando disponibilidad de productos...",
      });
      setStockValidationErrors([]);

      // Track checkout_progress event
      trackEcommerceEvent("checkout_progress", {
        currency: "PEN",
        value: cartGroups.reduce(
          (sum: number, group: any) => sum + (group.groupTotal || 0),
          0
        ),
        items: cartGroups.flatMap((group: any) =>
          group.items.map((item: any) => ({
            item_id: item.product?.id,
            item_name: item.product?.name || "Unknown Product",
            price: item.price?.discount?.newPrice || item.price || 0,
            quantity: item.quantity,
          }))
        ),
        checkout_step: 1,
        checkout_option: "Validation",
      });

      // Use the new combined validate and reserve stock API
      const reservationTime = 15; // 15 minutes reservation
      const validationResponse = await validateAndReserveStock(
        cartGroups,
        reservationTime
      );

      if (!validationResponse.isValid) {
        setStockValidationErrors(validationResponse.errors || []);
        setCheckoutValidationStatus({
          isValidating: false,
          isComplete: true,
          message: "Hay problemas con algunos productos.",
        });
        return validationResponse;
      }

      // If validation and reservation were successful
      if (validationResponse.isValid && validationResponse.isReserved) {
        // Set checkout session flags using the reservation expiry from response
        sessionStorage.setItem("checkoutSession", "true");
        sessionStorage.setItem(
          "checkoutReservationExpiry",
          validationResponse.reservationExpiry
        );
        sessionStorage.setItem("successfulCheckout", "true");

        setCheckoutValidationStatus({
          isValidating: true,
          isComplete: false,
          message: "Stock validado y reservado. Redirigiendo al checkout...",
        });

        // Return successful validation with updated cart groups
        return {
          isValid: true,
          updatedCartGroups: validationResponse.updatedCartGroups || cartGroups,
          errors: [],
        };
      } else {
        setCheckoutValidationStatus({
          isValidating: false,
          isComplete: true,
          message: "Error al validar o reservar stock.",
        });

        // Return failed validation with errors
        return {
          isValid: false,
          errors: validationResponse.errors || [
            "No se pudo validar o reservar el stock",
          ],
        };
      }
    } catch (error) {
      console.error("Checkout validation failed:", error);
      setCheckoutValidationStatus({
        isValidating: false,
        isComplete: true,
        message: "Error al validar productos. Intente nuevamente.",
      });
      return {
        isValid: false,
        errors: [
          "Ha ocurrido un error inesperado. Por favor, intente nuevamente.",
        ],
      };
    }
  };

  // Create a refresh cart function
  const refreshCart = useCallback(() => {
    dispatch(fetchCart());
  }, [dispatch]);

  // Display appropriate views based on cart state
  if (loading) return <CartSkeleton />;
  if (error) return <div>Error: {error}</div>;
  if (!cartGroups || !cartId || !cartGroups.length) return <EmptyCartView />;

  // Check if there are any items in the cart
  const hasItems = cartGroups.some(
    (group: any) => group.items && group.items.length > 0
  );

  if (!hasItems) return <EmptyCartView />;

  // Handle coupon removal
  const handleCouponRemoval = async () => {
    if (!cartId) {
      console.error("No cart ID available for coupon removal");
      return;
    }

    if (!appliedCouponCode) {
      console.error("No coupon code to remove");
      return;
    }

    try {
      await dispatch(
        removeCoupon({ cartId, couponCode: appliedCouponCode })
      ).unwrap();
      console.log("Coupon removed successfully");
      // Cart data is already updated by the thunk
    } catch (error) {
      console.error("Coupon removal failed:", error);
      // Error is already handled in Redux state
    }
  };

  // Handle coupon validation using Redux
  const handleCouponValidation = async (couponCode: string) => {
    if (!cartId) {
      console.error("No cart ID available for coupon validation");
      return;
    }

    // Check if the coupon is already applied to prevent duplicate application
    if (appliedCouponCode === couponCode) {
      console.log("Coupon is already applied:", couponCode);
      return;
    }

    // Clear any previous errors
    dispatch(clearCouponError());

    try {
      await dispatch(validateCoupon({ couponCode, cartId })).unwrap();
      console.log("Coupon validated successfully:", couponCode);
      // Cart data is already updated by the thunk
    } catch (error) {
      console.error("Coupon validation failed:", error);
      // Error is already handled in Redux state
    }
  };

  // Render the cart with items
  return (
    <div className="container">
      <div className={styles.cartContainer}>
        {isLocationUpdating && (
          <LoadingOverlay message="Actualizando información de envío..." />
        )}

        {checkoutValidationStatus.isValidating && (
          <LoadingOverlay message={checkoutValidationStatus.message} />
        )}

        <h1 className={styles.pageTitle}>Carrito</h1>

        <div className={styles.cartMain}>
          <div className={styles.cartContent}>
            <LocationInfo
              location={location}
              onEditLocation={() => console.log("Edit location clicked")}
            />

            {!location.address ? (
              <CartGroup
                group={{
                  id: "standby-group",
                  items: cartGroups.flatMap((group: any) => group.items),
                  subTotal: cartGroups.reduce(
                    (sum: number, group: any) => sum + (group.subTotal || 0),
                    0
                  ),
                  deliveryFee: cartGroups.reduce(
                    (sum: number, group: any) => sum + (group.deliveryFee || 0),
                    0
                  ),
                  groupTotals: {
                    originalSubtotal: cartGroups.reduce(
                      (sum: number, group: any) =>
                        sum +
                        (group.groupTotals?.originalSubtotal ||
                          group.subTotal ||
                          0),
                      0
                    ),
                    customizationTotal: cartGroups.reduce(
                      (sum: number, group: any) =>
                        sum + (group.groupTotals?.customizationTotal || 0),
                      0
                    ),
                    promotionalDiscountTotal: cartGroups.reduce(
                      (sum: number, group: any) =>
                        sum +
                        (group.groupTotals?.promotionalDiscountTotal ||
                          group.promotionalDiscountAmount ||
                          0),
                      0
                    ),
                    couponDiscountTotal: cartGroups.reduce(
                      (sum: number, group: any) =>
                        sum +
                        (group.groupTotals?.couponDiscountTotal ||
                          group.couponDiscountAmount ||
                          0),
                      0
                    ),
                    finalSubtotal: cartGroups.reduce(
                      (sum: number, group: any) =>
                        sum + (group.groupTotals?.finalSubtotal || 0),
                      0
                    ),
                    deliveryFeeTotal: cartGroups.reduce(
                      (sum: number, group: any) =>
                        sum +
                        (group.groupTotals?.deliveryFeeTotal ||
                          group.deliveryFee ||
                          0),
                      0
                    ),
                    groupTotal: cartGroups.reduce(
                      (sum: number, group: any) =>
                        sum +
                        (group.groupTotals?.groupTotal ||
                          group.groupTotal ||
                          0),
                      0
                    ),
                  },
                }}
                index={0}
                onDeliveryDateChange={handleDeliveryDateChange}
                refreshCart={refreshCart}
                handleCartAmountChange={handleCartAmountChange}
                handleRemoveItem={handleRemoveItem}
                isStandbyGroup={true}
              />
            ) : (
              cartGroups.map((group: any, index: number) => {
                // Extract affected product IDs from coupon details for this group
                const affectedProductIds =
                  couponDetails?.discount?.applicable_items?.map(
                    (item: any) => item.documentId || item.id
                  ) || [];

                return (
                  <CartGroup
                    key={group.id || `group-${index}`}
                    group={group}
                    index={index}
                    onDeliveryDateChange={handleDeliveryDateChange}
                    refreshCart={refreshCart}
                    handleCartAmountChange={handleCartAmountChange}
                    handleRemoveItem={handleRemoveItem}
                    stockValidation={stockValidation.results.find(
                      (result: any) => result.groupId === group.id
                    )}
                    appliedCouponCode={appliedCouponCode}
                    couponDetails={couponDetails}
                    affectedProductIds={affectedProductIds}
                  />
                );
              })
            )}
          </div>

          <div className={styles.cartSidebar}>
            <CartSummary
              isCheckoutDisabled={isCheckoutDisabled || isLocationUpdating}
              onProceedToCheckout={handleCheckoutValidation}
              key={`cart-summary-${cart.cartVersion}`}
            />
            <CouponCodeInput
              onValidate={handleCouponValidation}
              onRemove={handleCouponRemoval}
              loading={couponLoading}
              appliedCouponCode={appliedCouponCode}
              discountAmount={couponDiscountAmount}
              error={couponError}
            />
          </div>
        </div>

        {/* Stock validation errors */}
        {stockValidationErrors.length > 0 && (
          <div className={styles.errorContainer}>
            <h3>Problemas con disponibilidad de productos:</h3>
            <ul className={styles.errorList}>
              {stockValidationErrors.map((error, index) => (
                <li key={index} className={styles.errorItem}>
                  <div className={styles.errorIcon}>⚠️</div>
                  <div className={styles.errorContent}>
                    <p className={styles.errorText}>{error}</p>
                    {error.includes("insuficiente") && (
                      <button
                        className={styles.adjustQuantityButton}
                        onClick={() => {
                          // Find the product mentioned in the error and adjust its quantity
                          const productMatch =
                            error.match(/producto (.+?) tiene/);
                          if (productMatch && productMatch[1]) {
                            const productName = productMatch[1];
                            // Find the product in cart groups
                            for (const group of cartGroups) {
                              const product = group.items.find(
                                (p: any) => p.product.name === productName
                              );
                              if (product) {
                                // Extract available quantity from error message
                                const quantityMatch =
                                  error.match(/disponible: (\d+)/);
                                if (quantityMatch && quantityMatch[1]) {
                                  const availableQty = parseInt(
                                    quantityMatch[1],
                                    10
                                  );
                                  handleCartAmountChange(availableQty, product);
                                }
                                break;
                              }
                            }
                          }
                        }}
                      >
                        Ajustar a cantidad disponible
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
